import { useState } from 'react';
import type { ActivityWindow } from '@bbbk/schema';
import { VBX, VBY, ChartAxes, ChartTooltip, hoverIndexFromEvent, linePath,
  ChartFrame,
  ChartNote,  spacedTicks,

} from '../bookings/chartParts';
import { countAxis, type LoginPoint } from './accountCharts';
import { WINDOW_EMPTY_PHRASE } from './activityFormat';

// Sign-ins per day — a single brand line over a continuous (zero-filled) day
// axis. Same .chart-* chrome as the Finances trend chart: 0–100 viewBox SVG,
// HTML-overlaid axes, a hover guide-line + focus dot + shared tooltip. Counts on
// the y-axis (whole numbers, shared countAxis), sparse date ticks on the x-axis.
//
// The SVG's accessible name matches the card title word for word ("Sign-ins per
// day"), so the screen-reader name and the visible one are the same phrase; and
// `window` names the period in the empty state instead of "this window".
export function AccountLoginsLine({ points, window }: { points: LoginPoint[]; window: ActivityWindow }) {
  const [hi, setHi] = useState<number | null>(null);
  const n = points.length;
  const total = points.reduce((a, p) => a + p.count, 0);
  const { max: yMax, ticks: yTicks } = countAxis(Math.max(0, ...points.map((p) => p.count)));
  const xAt = (i: number) => (n <= 1 ? VBX / 2 : (i / (n - 1)) * VBX);
  const yAt = (v: number) => VBY - (v / yMax) * VBY;
  const line = linePath(n, (i) => [xAt(i), yAt(points[i]!.count)]);
  const area = `${line} L${xAt(n - 1).toFixed(2)},${VBY} L${xAt(0).toFixed(2)},${VBY} Z`;
  // Sparse date ticks (~5 across) so labels never crowd on a compact card.
  //
  // The shared rule from chartParts. This card is where the collision guard was
  // first worked out — privately, so three other charts kept printing their last
  // two labels on top of each other. It lives in one place now.
  const xTicks = spacedTicks(n, Math.ceil(n / 4)).map(({ i }) => ({ i, label: points[i]!.label }));
  const hp = hi != null ? points[hi]! : null;
  const leftPct = hi != null ? (hi / Math.max(1, n - 1)) * 100 : 0;
  const anchorPct = hp ? Math.max(0, Math.min(100, (yAt(hp.count) / VBY) * 100)) : 0;

  // Empty renders INSIDE the canvas, not as a dashed panel: a card whose chart
  // is one height and whose empty state is another makes the page jump the
  // moment the window changes.
  if (total === 0)
    return (
      <ChartFrame className="account-chart">
        <ChartNote>No sign-ins {WINDOW_EMPTY_PHRASE[window]}.</ChartNote>
      </ChartFrame>
    );

  return (
    <ChartFrame className="account-chart">
        <div
          className="chart-svg-wrap"
          onMouseLeave={() => setHi(null)}
          onMouseMove={(e) => setHi(hoverIndexFromEvent(e, n))}
        >
          <svg className="chart-svg" width="100%" height="100%" viewBox={`0 0 ${VBX} ${VBY}`} preserveAspectRatio="none" role="img" aria-label="Sign-ins per day">
            {yTicks.map((v, i) => (
              <line key={'g' + i} x1="0" y1={yAt(v)} x2={VBX} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            <path d={area} fill="var(--brand-soft)" opacity="0.5" stroke="none" />
            <path d={line} fill="none" stroke="var(--brand)" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            {hi != null && (
              <line x1={xAt(hi)} y1="0" x2={xAt(hi)} y2={VBY} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
            )}
          </svg>
          {hp && (
            <>
              <span className="chart-focus-dot" style={{ left: (xAt(hi!) / VBX) * 100 + '%', top: (yAt(hp.count) / VBY) * 100 + '%', background: 'var(--brand)' }} />
              <ChartTooltip
                head={hp.label}
                total={{ label: 'Sign-ins', value: hp.count }}
                leftPct={leftPct}
                anchorPct={anchorPct}
              />
            </>
          )}
        </div>
      <ChartAxes yTicks={yTicks} xTicks={xTicks} seriesLength={n} />
    </ChartFrame>
  );
}
